/**
 * The tone of a screen. Every screen of the Panel is light («día»); two are dark («noche») by the owner's orders:
 * Tarjetas (13 October 2026, docs/decisions/2026-10-13-panel-living-redesign.md, ruling 3) and the Constructor
 * (31 October 2026, docs/decisions/2026-10-31-constructor-three-table-types.md). There is no theme
 * switch and no stored preference: a screen is one tone or the other in its code.
 *
 * `data-tone="noche"` on a screen root redefines the tokens the shared pieces read (`_tone.scss`). A Radix portal
 * renders outside that root, so the dialog card and the sheet read the tone from React context, which crosses portals,
 * and carry the attribute themselves. The shell goes dark while a night screen is mounted, so the side rail is never a
 * bright strip beside a black page.
 *
 * This file holds what the Panel's entry reads (the shell and its dialogs). The screen root, `ToneScope`, lives in
 * `tone-scope.tsx`, so it ships with the screens that draw one, never in the entry.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

export type LivingTone = 'dia' | 'noche';

/** The tone a `ToneScope` gives the pieces inside it, portals included. */
export const LivingToneContext = createContext<LivingTone>('dia');

/** A shell hands this out: ask for the night tone, get back the function that gives it up. */
export type ShellToneRequest = (tone: LivingTone) => () => void;

/** Provided by `NavigationShell`; absent in a spec or a frame drawn without the shell. */
export const ShellToneContext = createContext<ShellToneRequest | null>(null);

/** The tone of the nearest `ToneScope`; «día» outside one. */
export const useLivingTone = (): LivingTone => useContext(LivingToneContext);

/** The shell's navigation in its tone: what it opens in a portal (the notices) reads it with `useLivingTone`. */
export function NavigationTone({ tone, children }: { readonly tone: LivingTone; readonly children: ReactNode }) {
  return <LivingToneContext.Provider value={tone}>{children}</LivingToneContext.Provider>;
}

/** The attribute a root, or anything rendered in a portal, carries to read the tone's tokens. */
export function toneAttributes(tone: LivingTone): { readonly 'data-tone'?: 'noche' } {
  return tone === 'noche' ? { 'data-tone': 'noche' } : {};
}
